import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useInterview } from '@/hooks/use-interview'
import { uploadResume, getToken, getPlan, startInterviewFromProfile, getCandidateProfile, apiErrorMessage } from '@/api/client'
import { InterviewAgent } from '@/components/voice/InterviewAgent'
import { VoicePicker } from '@/components/interview/VoicePicker'
import { useVoiceCatalog } from '@/hooks/use-voice-catalog'
import { LoadingButton } from '@/components/spectrumui/loading-button'
import { ResumeDropzone } from '@/components/ResumeDropzone'
import { Toaster } from '@/components/ui/toaster'
import { useToast } from '@/hooks/use-toast'
import { AppShell } from '@/components/layout/AppShell'
import { usePageMeta } from '@/hooks/use-page-meta';

export function InterviewFlow() {
  usePageMeta("New interview — Aura")
  const { step, sessionId, plan, startPreview, startInterview, reset } = useInterview()
  const [file, setFile] = useState<File | null>(null)
  const [jobDescription, setJobDescription] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [hasResume, setHasResume] = useState(false)
  const [isStartingFromProfile, setIsStartingFromProfile] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const { voices, voiceId, handleVoiceChange, resolveJoinVoice } = useVoiceCatalog()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Deep link: /interview?session=<id> skips the upload step and shows the
  // plan (used by "start interview from profile" and returning users).
  useEffect(() => {
    const existing = searchParams.get('session')
    if (!existing) return
    setSearchParams({}, { replace: true })
    getPlan(existing)
      .then(({ plan: fetched }) => {
        startPreview({ session_id: existing, plan_summary: fetched })
        toast({ title: 'Interview plan loaded', description: `Plan ready for ${fetched.candidate_name}.` })
      })
      .catch(() => {
        toast({ title: 'Could not load that session', description: 'Start a new interview instead.', variant: 'destructive' })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Offer "use my saved resume" once a profile resume is on file.
  useEffect(() => {
    if (step !== 'UPLOAD') return
    let cancelled = false
    getCandidateProfile()
      .then(p => { if (!cancelled) setHasResume(p.resume_stored) })
      .catch(() => { /* profiles are optional here; the upload flow still works */ })
    return () => { cancelled = true }
  }, [step])

  const handleUseProfileResume = async () => {
    setIsStartingFromProfile(true)
    try {
      const data = await startInterviewFromProfile()
      startPreview(data)
      toast({ title: 'Resume parsed', description: `Plan ready for ${data.plan_summary.candidate_name}.` })
    } catch (error) {
      const msg = axios.isAxiosError(error) && error.response?.status === 429
        ? 'Rate limit reached. Please try again later.'
        : axios.isAxiosError(error) && error.response?.status === 404
          ? 'No resume on your profile yet — upload one below or add it on your profile page.'
          : 'Could not prepare the interview from your saved resume.'
      toast({ title: 'Not possible', description: msg, variant: 'destructive' })
    } finally {
      setIsStartingFromProfile(false)
    }
  }

  const handleUpload = async () => {
    if (!file) return
    setIsUploading(true)
    try {
      const data = await uploadResume(file, jobDescription)
      startPreview(data)
      toast({ title: "Resume parsed", description: `Plan ready for ${data.plan_summary.candidate_name}.` })
    } catch (error) {
      const msg = axios.isAxiosError(error) && error.response?.status === 429
        ? "Rate limit reached. Please try again later."
        : error instanceof Error ? error.message : "Upload a valid PDF."
      toast({ title: "Upload failed", description: msg, variant: "destructive" })
    } finally { setIsUploading(false) }
  }

  const handleJoin = async () => {
    if (!sessionId) return
    setIsConnecting(true)
    try {
      const t = await getToken(sessionId, resolveJoinVoice())
      setToken(t)
      startInterview()
    } catch (error) {
      // apiErrorMessage surfaces the server's own detail — the 409s for an
      // already-started/completed session and slowapi's 429 body all read as
      // plain sentences instead of a generic "could not get access token".
      const msg = apiErrorMessage(error, "Could not get access token. Please try again.")
      toast({ title: "Connection failed", description: msg, variant: "destructive" })
    } finally { setIsConnecting(false) }
  }

  // Interview takes over fullscreen. When it ends — confirmed end or the
  // worker closing the room — the candidate goes straight to the session's
  // report page, which polls until the background report lands.
  if (step === 'INTERVIEW' && token && sessionId) {
    return (
      <InterviewAgent
        token={token}
        sessionId={sessionId}
        candidateName={plan?.candidate_name}
        recordAudio
        onInterviewEnd={() => navigate(`/my-interviews/${sessionId}`, { replace: true })}
      />
    )
  }

  return (
    <AppShell active="new-interview">
      <Toaster />
      {/* Keyed by step: UPLOAD↔PREVIEW swaps remount the grid, so the shell's
          page-rise bridges the step change instead of content teleporting. */}
      <div className="wnew-grid" key={step}>
        <header className="screen-head">
          <div>
            <h1>{step === 'PREVIEW' ? 'Ready when you are.' : 'Build your next interview'}</h1>
            <p>
              {step === 'PREVIEW'
                ? "Here's what Aura will explore in the conversation. Start the interview when you're ready."
                : "Bring your resume and the job you're targeting. Aura turns the context into a focused practice session."}
            </p>
          </div>
        </header>

        {step === 'UPLOAD' && (
          <>
            <section className="panel wform-panel" aria-label="Interview setup">
              <div className="field">
                <label>Resume file<span className="required" aria-hidden="true">*</span></label>
                <ResumeDropzone
                  title="Drop your PDF here, or browse"
                  description="Nothing uploads until you prepare the interview."
                  hint="PDF only · max 10 MB"
                  fileName={file?.name ?? null}
                  onReject={message => toast({ title: "Can't use that file", description: message, variant: 'destructive' })}
                  onFile={setFile}
                />
                <span className="hint">Replaces the resume on your profile for this session.</span>
              </div>

              <div className="field">
                <label htmlFor="job-description">Job description <span className="hint font-medium">Optional</span></label>
                <textarea
                  id="job-description"
                  placeholder="Paste the job posting to align questions with the listing…"
                  value={jobDescription}
                  onChange={e => setJobDescription(e.target.value)}
                  rows={4}
                  maxLength={4000}
                />
                <span className="hint">Questions use the job description when available.</span>
              </div>

              <div className="form-actions wform-actions">
                <LoadingButton variant="secondary" size="lg" loading={isStartingFromProfile} disabled={!hasResume} onClick={handleUseProfileResume}>
                  Use my saved resume
                </LoadingButton>
                <LoadingButton size="lg" loading={isUploading} disabled={!file} onClick={handleUpload}>
                  Prepare interview
                </LoadingButton>
              </div>
            </section>

            <aside className="wnew-aside" aria-label="How it works">
              <section className="panel waside-card">
                <h2>How it works</h2>
                <div className="wsteps">
                  <div className="wstep"><span className="wstep-num" aria-hidden="true">1</span><div><strong>Grounding</strong><span>Resume + job description set the question pool.</span></div></div>
                  <div className="wstep"><span className="wstep-num" aria-hidden="true">2</span><div><strong>Live session</strong><span>Answer out loud — up to 10 minutes.</span></div></div>
                  <div className="wstep"><span className="wstep-num" aria-hidden="true">3</span><div><strong>Report</strong><span>Scores, feedback, and retry targets.</span></div></div>
                </div>
              </section>
            </aside>
          </>
        )}

        {step === 'PREVIEW' && plan && (
          <>
            <section className="panel wform-panel" aria-label="Interview plan">
              <div className="wpreview-head">
                <span className="avatar" aria-hidden="true">{plan.candidate_name.split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase() || 'C'}</span>
                <div>
                  <strong className="wpreview-name">{plan.candidate_name}</strong>
                  <span className="hint">Voice interview · {plan.question_bank.length} question{plan.question_bank.length === 1 ? '' : 's'} · up to 10 min</span>
                </div>
              </div>

              <div className="field">
                <span className="field-label">Identified skills</span>
                <div className="wchips">
                  {plan.extracted_skills.map((s, i) => <span key={i} className="chip is-active is-static">{s}</span>)}
                </div>
              </div>

              <VoicePicker voices={voices} value={voiceId} onChange={handleVoiceChange} />

              <div className="field">
                <p className="wnote" role="note">
                  Your microphone is recorded for this practice session.
                </p>
              </div>

              <div className="form-actions wform-actions">
                <button className="wbtn wbtn-secondary" onClick={reset}>Cancel</button>
                <LoadingButton size="lg" loading={isConnecting} onClick={handleJoin}>
                  Start voice interview
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                </LoadingButton>
              </div>
            </section>

            <aside className="wnew-aside" aria-label="What happens next">
              <section className="panel waside-card">
                <h2>What happens next</h2>
                <div className="wsteps">
                  <div className="wstep"><span className="wstep-num" aria-hidden="true">1</span><div><strong>Voice conversation</strong><span>Aura asks; you answer out loud, naturally.</span></div></div>
                  <div className="wstep"><span className="wstep-num" aria-hidden="true">2</span><div><strong>Follow-ups</strong><span>Answers are probed deeper as you go.</span></div></div>
                  <div className="wstep"><span className="wstep-num" aria-hidden="true">3</span><div><strong>Instant report</strong><span>Scores and feedback the moment you finish.</span></div></div>
                </div>
              </section>
            </aside>
          </>
        )}
      </div>
    </AppShell>
  )
}
